import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ICustomer, ICustomerAddressForm, ICustomerAddressList, ICustomerCommissions, ICustomerForm, ICustomerReferentForm, ICustomerReferentList } from "./customer.interface";
import moment from "moment";
import { ListService } from "../../auth/service/list.service";
import { CitiesService } from "../../auth/service/cities.service";

@Injectable({
  providedIn: "root",
})
export class CustomerService {
  public addresses: ICustomerAddressList[] = [];
  public customersList: ICustomer[] = [];
  public referencesList: ICustomerReferentList[] = [];

  constructor(
    private _httpClient: HttpClient,
    private _listService: ListService,
    private _citiesService: CitiesService,
  ) {}

  async getCustomers() {
    return this._httpClient
      .get("/client")
      .toPromise()
      .then((response: ICustomer[]) => {
        this.customersList = response;
        return response;
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  async getCustomer(id: number) {
    return this._httpClient
      .get("/client/" + id)
      .toPromise()
      .catch((err) => {
        return err;
      });
  }

  async editCustomer(id: number, data: ICustomerForm | { logo: string }) {
    return this._httpClient
      .put("/client/" + id, data)
      .toPromise()
      .then(() => {
        this.getReferents();
        this.getAddresses();
        this.getCustomers();
      });
  }

  async addCustomer(data: ICustomerForm) {
    return this._httpClient
      .post("/client", data)
      .toPromise()
      .then((response: { id: number }) => {
        this.getReferents();
        this.getAddresses();
        this.getCustomers();
        return response;
      });
  }

  async calculateDistanceForCustomerToUs(id: number, data = {}) {
    return this._httpClient
      .post("/client/distance-from-us/" + id, data)
      .toPromise()
      .catch((error) => {
        console.error(error);
        throw new Error(error.message);
      });
  }

  async deleteCustomer(id: number) {
    return this._httpClient
      .delete("/client/" + id)
      .toPromise()
      .then(() => {
        this.getReferents();
        this.getAddresses();
        this.getCustomers();
      });
  }

  // address
  async getAddresses() {
    return this._httpClient
      .get("/client-address")
      .toPromise()
      .then((response: ICustomerAddressList[]) => {
        response = response.filter((item: ICustomerAddressList) => !!item.client)
        this.addresses = response;
        return response;
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  async getAddress(id: number) {
    return this._httpClient.post<ICustomerAddressList>("/client-address/" + id, {}).toPromise();
  }

  async editAddress(id: number, data: ICustomerAddressForm) {
    return this._httpClient
      .put("/client-address/" + id, data)
      .toPromise()
      .then(() => {
        this.getReferents();
        this.getCustomers();
        this.getAddresses();
      });
  }

  async addAddress(data: ICustomerAddressForm) {
    return this._httpClient
      .post("/client-address", data)
      .toPromise()
      .then((response: { site_id: number }) => {
        this.getReferents();
        this.getCustomers();
        this.getAddresses();
        return response;
      });
  }

  async deleteAddress(id: number) {
    return this._httpClient
      .delete("/client-address/" + id)
      .toPromise()
      .then(() => {
        this.getReferents();
        this.getCustomers();
        this.getAddresses();
      });
  }

  // referent
  async getReferents() {
    return this._httpClient
      .get("/client-referent")
      .toPromise()
      .then((response: ICustomerReferentList[]) => {
        this.referencesList = response;
        return response;
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  async editReferent(id: number, data: ICustomerReferentForm) {
    return this._httpClient
      .put("/client-referent/" + id, data)
      .toPromise()
      .then(() => {
        this.getAddresses();
        this.getCustomers();
        this.getReferents();
      });
  }

  async addReferent(data: ICustomerReferentForm) {
    return this._httpClient
      .post("/client-referent", data)
      .toPromise()
      .then((response: ICustomerReferentForm) => {
        this.getAddresses();
        this.getCustomers();
        this.getReferents();
        return response;
      });
  }

  async deleteReferent(id: number) {
    return this._httpClient
      .delete("/client-referent/" + id)
      .toPromise()
      .then(() => {
        this.getAddresses();
        this.getCustomers();
        this.getReferents();
      });
  }

  //Restituisce le commesse di un cliente con le statistiche economiche
  async getCustomerCommissionAnalytics(id: number, year: number) {
    return this._httpClient.get<ICustomerCommissions[]>(`/client/analytics/${id}/${year}`).toPromise();
  }

  //Restituisce le commesse di tutti i clienti con le statistiche economiche
  async getCustomersCommissionAnalytics() {
    return this._httpClient
      .get("/client/analytics/" + moment().year())
      .toPromise()
      .catch((err) => {
        return err;
      });
  }

  async getCustomerEconomicGraph(client_id: number, year: number) {
    return this._httpClient.get(`/client/profit-graphic/${client_id}/${year}`).toPromise();
  }

  async fillDynamicFields(fieldsInvoice: any) {
    const banks: { label: string; value: string }[] = await this._listService.getList("bank").then((list: any) => list.map((item: any) => ({ label: item.name, value: item.id })));
    const invoiceType: { label: string; value: string }[] = await this._listService.getList("invoice_document_type").then((list: any) => list.map((item: any) => ({ label: item.name, value: item.id })));
    const fteSubjectType: { label: string; value: string }[] = await this._listService.getList("fte_subject_type").then((list: any) => list.map((item: any) => ({ label: item.name, value: item.id })));
    const paymentTerms: { label: string; value: string }[] = await this._listService.getList("payment_terms").then((list: any) => list.map((item: any) => ({ label: item.name, value: item.id })));

    const trackFinancialRelevance: { label: string; value: string }[] = await this._listService.getList("customer_financial_relevance").then((list: any) =>
      list.map((item: any) => ({
        label: item.name,
        value: item.id,
      })),
    );
    
    const cities: { label: string; value: string }[] = this._citiesService.list.map((item: any) => ({ label: `${item.name} (${item.initials})`, value: item.id }));

    return fieldsInvoice.map((field: any) => {
      switch (field.key) {
        case "bank":
          field.values = banks;
          break;
        case "invoice_type":
          field.values = invoiceType;
          break;
        case "payment_terms":
          field.values = paymentTerms;
          break;
        case "fte_type_subject":
          field.values = fteSubjectType;
          break;
        case "track_financial_relevance":
          field.values = trackFinancialRelevance;
          break;
        case "person_birth_city":
          field.values = cities;
          break;
      }
      return field;
    });
  }
}
